<template>

<section class="container my-2">

<div class="board shadow">

  <h3>Form</h3>
  <p class="display-8">A form is comprised of custom components and helper classes for customizable, responsive layout with a consistent look and feel.
  </p>
  <p class="display-10">By default, Symphony forms are themed in shades of blue (via config setting). However, this default blue 'theme' can be overridden via the 'themeId' attribute on the form tag itself.
    <br><br>Five (5) theme colors are available:
    <ul class='ml-4'>
      <li><b>info</b> - blue (default)</li>
      <li><b>success</b> - green</li>
      <li><b>warning</b> - orange/yellow</li>
      <li><b>dark</b> - black</li>
      <li><b>primary</b> - purple</li>
    </ul>

  </p>

  <br><hr>

  <p class='bold'>● Basic form - Forms are marked using the 'sym-form' tag. Controls need to be wrapped in a div element with
     class 'grid' for responsive layout.
  </p>

  <sym-form id="sign-in" caption="Sign In">
    <sym-exceptions :exceptions="formExceptions('sign-in')">
    </sym-exceptions>

    <div class="grid rows-2 mb-1">
      <sym-text v-model="email" :caption-width="23" :input-width="41"></sym-text>
      <sym-text v-model="password" :caption-width="23" :input-width="41" type="password"></sym-text>
    </div>
    <button type="button" class="info fw-24 text-center" @click="onSignIn">Submit</button>
  </sym-form>

  <br>

  <textarea class="code" disabled wrap="off" spellcheck="false" rows="12">
  <sym-form id="sign-in" caption="Sign In">
    <sym-exceptions :exceptions="formExceptions('sign-in')">
    </sym-exceptions>

    <div class="grid rows-2 mb-1">
      <sym-text v-model="email" :caption-width="23" :input-width="41"></sym-text>
      <sym-text v-model="password" :caption-width="23" :input-width="41" type="password"></sym-text>
    </div>
    <button type="button" class="info fw-24 text-center" @click="onSignIn">Submit1</button>
  </sym-form>
  </textarea>

  <br><hr>

  <p class='bold'>● Form - 2 columns, responsive</p>

  <sym-form id="res" caption="Reservation" themeId="success">
    <sym-exceptions :exceptions="formExceptions('res')">
    </sym-exceptions>

    <div class="grid rows-2 col-gap-3 mb-1">
      <div>
        <sym-text v-model="res.lastName" :caption-width="25" :input-width="44"></sym-text>
        <sym-text v-model="res.firstName" :caption-width="25" :input-width="44"></sym-text>
      </div>
      <div>
        <sym-date v-model="res.arriveDate" :caption-width="25" :input-width="26" @changed="onArriveDateChanged"></sym-date>
        <sym-date v-model="res.departDate" :caption-width="25" :input-width="26" @changed="onDepartDateChanged"></sym-date>
        <sym-int v-model="res.nights" :caption-width="25" :input-width="16" @changed="onNightsChanged" :blank-zero="false"></sym-int>
        <sym-radio v-model="res.leaseTypeId" :caption-width="25" :input-width="61" display-field="leaseTypeName" :datasource="leaseTypes" align="bottom" vertical @changing="onLeaseTypeIdChanging"></sym-radio>
      </div>
      <div>
        <sym-combo v-model="res.roomTypeId" :caption-width="25" :input-width="36" display-field="roomTypeName" :datasource="roomTypes" @changed="onRoomTypeIdChanged" @changing="onRoomTypeIdChanging"></sym-combo>
        <sym-dec v-model="res.roomRate" :caption-width="25" :input-width="36"></sym-dec>
      </div>
      <div>
        <sym-check v-model="res.vipFlag" :caption-width="25" plainxx align="bottomxx" inputBoxClass="pt-2xx"></sym-check>
        <sym-memo v-model="res.remarks" :input-width="61" :input-height="3"></sym-memo>
      </div>
    </div>

    <button type="button" class="success fw-26 text-center shadow-soft mt-1" @click="onSaveReservation">Save</button>
  </sym-form>

  <br>

  <textarea class="code" disabled wrap="off" spellcheck="false" rows="30">
  <sym-form id="res" caption="Reservation" themeId="success">
    <sym-exceptions :exceptions="formExceptions('res')">
    </sym-exceptions>

    <div class="grid rows-2 col-gap-3 mb-1">
      <div>
        <sym-text v-model="res.lastName" :caption-width="25" :input-width="44"></sym-text>
        <sym-text v-model="res.firstName" :caption-width="25" :input-width="44"></sym-text>
      </div>
      <div>
        <sym-date v-model="res.arriveDate" :caption-width="25" :input-width="26" @changed="onArriveDateChanged"></sym-date>
        <sym-date v-model="res.departDate" :caption-width="25" :input-width="26" @changed="onDepartDateChanged"></sym-date>
        <sym-int v-model="res.nights" :caption-width="25" :input-width="16" @changed="onNightsChanged" :blank-zero="false"></sym-int>
        <sym-radio v-model="res.leaseTypeId" :caption-width="25" :input-width="61" display-field="leaseTypeName" :datasource="leaseTypes" align="bottom" vertical @changing="onLeaseTypeIdChanging"></sym-radio>
      </div>
      <div>
        <sym-combo v-model="res.roomTypeId" :caption-width="25" :input-width="36" display-field="roomTypeName" :datasource="roomTypes" @changed="onRoomTypeIdChanged" @changing="onRoomTypeIdChanging"></sym-combo>
        <sym-dec v-model="res.roomRate" :caption-width="25" :input-width="36"></sym-dec>
      </div>
      <div>
        <sym-check v-model="res.vipFlag" :caption-width="25"></sym-check>
        <sym-memo v-model="res.remarks" :input-width="61" :input-height="3"></sym-memo>
      </div>
    </div>

    <button type="button" class="success fw-26 text-center shadow-soft mt-1" @click="onSaveReservation">Save</button>
  </sym-form>
  </textarea>
</div>

</section>

</template>

<script>

import
  PageBase
from '../PageBase.vue';

export default {
  extends: PageBase,
  name: 'samples-form',

  data () {
    return {
      email: '',
      password: '',

      res: {
        lastName: '',
        firstName: '',
        // accountId: 0,
        nights: 0,
        roomTypeId: '',
        roomRate: 0,
        remarks: '',
        arriveDate: null,
        departDate: null,
        vipFlag: false,
        leaseTypeId: 0
      }
    };
  },

  computed: {
    arriveDayName () {
      return this.res.arriveDate ? this.res.arriveDate.format('dddd') : '';
    },

    departDayName () {
      return this.res.departDate ? this.res.departDate.format('dddd') : '';
    }

  },

  methods: {
    onRoomTypeIdChanging (e) {
      if (e.proposedValue === 'EXE') {
        e.preventDefault();
      }
    },

    onRoomTypeIdChanged (value, colName) {
      const roomType = this.core.getArrayItem(this.roomTypes, colName, value);
      this.res.roomRate = roomType.roomRate;
    },

    onArriveDateChanged (value) {
      const res = this.res;
      if (value > res.departDate) {
        res.departDate = value;
      }
      res.nights = res.departDate.subtractDate(value).days;
    },

    onNightsChanged (value) {
      const res = this.res;
      res.departDate = res.arriveDate.addDays(value);
    },

    onDepartDateChanged (value) {
      const res = this.res;
      if (value < res.arriveDate) {
        res.arriveDate = value;
      }
      res.nights = value.subtractDate(res.arriveDate).days;
    },

    onLeaseTypeIdChanging (e) {
      if (e.proposedValue === 3) {
        e.preventDefault();
        return;
      }
    },

    onSignIn () {
      if (!this.isValid('sign-in')) {
        return;
      }

      // do logon here
    },

    onSaveReservation () {
      const
        me = this,
        res = me.res,
        meta = me.meta;

      meta.remarks.mode = res.vipFlag && !res.remarks ? 'R' : '';

      if (!me.isValid('res')) {
        return;
      }

      // do update here
    }
  },

  created () {

    // non-reactive data

    this.roomTypes = [
      {
        roomTypeId: 'DLX',
        roomTypeName: 'De Luxe',
        roomRate: 3000.00
      },
      {
        roomTypeId: 'EXE',
        roomTypeName: 'Executive',
        roomRate: 3500.00
      },
      {
        roomTypeId: 'STE',
        roomTypeName: 'Suite',
        roomRate: 5000.00
      }
    ];

    this.leaseTypes = [
      {
        leaseTypeId: 1,
        leaseTypeName: 'Short Stay'
      },
      {
        leaseTypeId: 2,
        leaseTypeName: 'Long Stay'
      },
      {
        leaseTypeId: 3,
        leaseTypeName: 'Serviced Apartment'
      },
    ];
  },

  mounted () {
    const
      me = this,
      res = me.res,
      meta = me.meta;

    // meta.arriveDate.minValue = me.sym.defaultDates.today;
    meta.arriveDate.minValue = me.core.newDate(2019, 6, 27);

    meta.email.mode = 'R';
    meta.password.mode = 'R';

    meta.arriveDate.mode = 'R';
    meta.departDate.mode = 'R';
    meta.roomTypeId.mode = 'R';
    meta.leaseTypeId.mode = 'R';

    res.arriveDate = me.sym.defaultDates.today;
    res.departDate = me.res.arriveDate.addDays(1);
    res.nights = 1;
  }
}

</script>

<style scoped>

#sign-in {
  max-width: 25.125rem;
}

#res {
  max-width: 36.5rem;
}

@media (max-width: 576px) {
  #res >>> #body {      /* deep selector */
    padding: .5rem !important;
  }
  #res {
    max-width: 20.25rem;
  }
}

@media (max-width: 810px) {
  .grid {
    display: block;
  }
}

</style>
